<template>
  <!-- The sections of the resume, as icons with a line after each: a slim column beside the form on
       wide screens, a row above it on small ones. A tooltip names each one. Picking one shows its form. -->
  <nav aria-label="Resume sections" @scroll.passive="hideTip"
    class="flex md:flex-col items-center gap-1 p-2 md:px-0 md:py-2 overflow-x-auto md:overflow-x-visible md:overflow-y-auto md:w-14 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128]">
    <template v-for="item in items" :key="item.key">
      <button type="button" @click="$emit('select', item.key)"
        @mouseenter="showTip($event, item)" @mouseleave="hideTip" @focus="showTip($event, item)" @blur="hideTip"
        :aria-label="item.tip" :aria-current="active === item.key ? 'true' : undefined"
        class="relative flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
        :class="active === item.key
          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300'
          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-800 dark:hover:text-gray-100'">
        <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" :d="item.icon"/>
        </svg>
        <span v-if="item.count" class="absolute top-0.5 right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-blue-600 text-white text-[9px] font-bold leading-[15px] text-center tabular-nums" aria-hidden="true">{{ item.count }}</span>
      </button>
      <!-- A line after each one: edge to edge in the column, upright in the row -->
      <hr class="flex-shrink-0 border-0 bg-gray-200 dark:bg-gray-700 w-px h-6 mx-0.5 md:w-full md:h-px md:mx-0 md:self-stretch" aria-hidden="true">
    </template>

    <button v-if="canAdd" ref="addButton" type="button" @click="startAdding" @mouseenter="showTip($event, { tip: 'Add a section' })" @mouseleave="hideTip" @focus="showTip($event, { tip: 'Add a section' })" @blur="hideTip"
      aria-label="Add a section" aria-haspopup="dialog" :aria-expanded="adding"
      class="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50">
      <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" :d="ICONS.add"/></svg>
    </button>
  </nav>

  <!-- Both float over the page, so a column that scrolls cannot clip them -->
  <Teleport to="body">
    <div v-if="tip" role="tooltip" class="fixed z-50 px-2 py-1 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-medium whitespace-nowrap shadow-lg pointer-events-none" :style="tip.style">{{ tip.text }}</div>

    <form v-if="adding" role="dialog" aria-label="Add a section" @submit.prevent="submit" @keydown.esc="adding = false"
      class="fixed z-50 flex items-center gap-1.5 p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg" :style="popoverStyle">
      <input ref="input" v-model="name" type="text" maxlength="80" placeholder="Section name, for example Awards" aria-label="New section name"
        class="w-56 h-8 px-2.5 text-[13px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400">
      <button type="submit" :disabled="!name.trim()" class="h-8 px-3 rounded-lg text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Add</button>
    </form>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { SECTION_LABELS } from './sectionDefs.js'

const props = defineProps({
  content: { type: Object, required: true },
  active: { type: String, required: true },
  canAdd: { type: Boolean, default: true },
})
const emit = defineEmits(['select', 'add'])

// Heroicons (outline), one per section.
const ICONS = {
  basics: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z',
  summary: 'M3.75 6.75h16.5M3.75 12H12m-8.25 5.25h16.5',
  experience: 'M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 0 0 .75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 0 0-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0 1 12 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 0 1-.673-.38m0 0A2.18 2.18 0 0 1 3 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 0 1 3.413-.387m7.5 0V5.25A2.25 2.25 0 0 0 13.5 3h-3a2.25 2.25 0 0 0-2.25 2.25v.894m7.5 0a48.667 48.667 0 0 0-7.5 0M12 12.75h.008v.008H12v-.008Z',
  education: 'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5',
  skills: 'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z',
  projects: 'M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5',
  certifications: 'M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z',
  languages: 'm10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802',
  custom: 'M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25H6A2.25 2.25 0 0 0 3.75 6v2.25A2.25 2.25 0 0 0 6 10.5Zm0 9.75h2.25A2.25 2.25 0 0 0 10.5 18v-2.25a2.25 2.25 0 0 0-2.25-2.25H6a2.25 2.25 0 0 0-2.25 2.25V18A2.25 2.25 0 0 0 6 20.25Zm9.75-9.75H18a2.25 2.25 0 0 0 2.25-2.25V6A2.25 2.25 0 0 0 18 3.75h-2.25A2.25 2.25 0 0 0 13.5 6v2.25a2.25 2.25 0 0 0 2.25 2.25Z',
  add: 'M12 4.5v15m7.5-7.5h-15',
}
const LIST_KEYS = ['experience', 'education', 'skills', 'projects', 'certifications', 'languages']

const entry = (key, label, icon, count = 0) => ({ key, icon, count, tip: count ? `${label} (${count})` : label })

const items = computed(() => [
  entry('basics', 'Personal details', ICONS.basics),
  entry('summary', SECTION_LABELS.summary, ICONS.summary),
  ...LIST_KEYS.map((key) => entry(key, SECTION_LABELS[key], ICONS[key], props.content[key].length)),
  ...props.content.custom.map((section) => entry(section.id, section.title || 'Untitled section', ICONS.custom, section.entries.length)),
])

// Tooltip: beside the button in the column, under it in the row.
const tip = ref(null)
const isColumn = () => window.matchMedia?.('(min-width: 768px)').matches

function placeAt(target) {
  const rect = target.getBoundingClientRect()
  return isColumn()
    ? { left: `${rect.right + 8}px`, top: `${rect.top + rect.height / 2}px`, transform: 'translateY(-50%)' }
    : { left: `${rect.left + rect.width / 2}px`, top: `${rect.bottom + 6}px`, transform: 'translateX(-50%)' }
}

function showTip(event, item) {
  if (adding.value) return
  tip.value = { text: item.tip, style: placeAt(event.currentTarget) }
}
const hideTip = () => { tip.value = null }

// Adding a section asks for its name in a small box next to the button.
const adding = ref(false)
const name = ref('')
const input = ref(null)
const addButton = ref(null)
const popoverStyle = ref({})

function startAdding() {
  hideTip()
  name.value = ''
  const rect = addButton.value.getBoundingClientRect()
  popoverStyle.value = isColumn()
    ? { left: `${rect.right + 8}px`, top: `${Math.max(8, rect.top - 6)}px` }
    : { left: `${Math.max(8, rect.left - 120)}px`, top: `${rect.bottom + 8}px` }
  adding.value = true
  nextTick(() => input.value?.focus())
}

function submit() {
  const title = name.value.trim()
  adding.value = false
  if (title) emit('add', title)
}

// A click anywhere else closes the box.
function onOutside(event) {
  if (!event.target.closest?.('form[aria-label="Add a section"]') && !addButton.value?.contains(event.target)) adding.value = false
}
watch(adding, (isOpen) => {
  if (isOpen) document.addEventListener('pointerdown', onOutside)
  else document.removeEventListener('pointerdown', onOutside)
})
onBeforeUnmount(() => document.removeEventListener('pointerdown', onOutside))
</script>
